const ascii = require('../ascii');
const gs1 = require('../gs1');
const types = require('../types');

/**
 * @typedef {Object} RfidConfig
 * @property {number} maxShortLength
 * @property {string} shortStrategy
 * @property {string} longStrategy
 * @property {string} displayStrategy
 * @property {boolean} strip0s
 */

/** Valid strategy identifiers for RFID tag encoding / display */
const STRATEGIES = Object.freeze({
    ASCII: 'ascii',
    HEX: 'hex',
    GS1: 'gs1',
    GID: 'gid',
    ID: '_id'
});

/** Default RFID config — RedBeam / ascii strategy */
const DEFAULTS = {
    maxShortLength: 12,
    shortStrategy: 'ascii',
    longStrategy: '_id',
    displayStrategy: 'hex'
};

/** Default RFID config — hex strategy */
const HEX_DEFAULTS = {
    maxShortLength: 24,
    shortStrategy: 'hex',
    longStrategy: 'hex',
    displayStrategy: 'hex'
};

/**
 * Checks whether a value is a non-empty hex string.
 * @param {*} value
 * @returns {boolean}
 */
function isValidHex(value) {
    return typeof value === 'string' && value.length > 0 && /^[0-9a-fA-F]+$/.test(value);
}

/**
 * True when every character of `str` is printable ASCII (0x20-0x7E).
 * @param {string} str
 * @returns {boolean}
 */
function isPrintableAscii(str) {
    // eslint-disable-next-line no-control-regex
    return typeof str === 'string' && /^[\x20-\x7E]*$/.test(str);
}

/**
 * True when a stored hex tagId can be *unambiguously* decoded to printable
 * ASCII: the decode is all-printable AND re-encoding reproduces the stored hex
 * (ignoring 00 padding). Without this, raw-hex tags decode to gibberish (bytes
 * like 0x96 are dropped/garbled), so we keep the raw hex instead.
 * @param {string} tagId
 * @returns {boolean}
 */
function canDisplayAsAscii(tagId) {
    if (typeof tagId !== 'string' || tagId === '') return false;
    const decoded = ascii.fromHex(tagId);
    if (!isPrintableAscii(decoded)) return false;
    const reEncoded = ascii.toHex(decoded).toUpperCase();
    const normalized = tagId.toUpperCase().replace(/^(00)+/, '').replace(/(00)+$/, '');
    return reEncoded === normalized;
}

/**
 * True when a stored hex tagId is *confidently* a GS1/EPC encoding: it parses to
 * a known EPC type AND re-encodes (`.hex`) back to the same value. The round-trip
 * rejects coincidences where a non-GS1 value happens to start with a recognized
 * EPC header byte but zero-pads to a different canonical hex on re-encode.
 * @param {string} tagId
 * @returns {boolean}
 */
function isLikelyGs1Hex(tagId) {
    if (typeof tagId !== 'string' || tagId.length <= 2) return false;
    try {
        const parsed = gs1.parse(tagId);
        return !!parsed && typeof parsed.hex === 'string' && parsed.hex.toUpperCase() === tagId.toUpperCase();
    } catch {
        return false;
    }
}

/**
 * Normalizes a hex tagId string:
 * - Uppercases the value
 * - For ascii strategy: strips leading and trailing `00` byte pairs (always, regardless of strip0s)
 * - For other strategies: strips leading zeros only when `rfidConfig.strip0s` is true
 * - No config or strip0s=false: uppercase only
 *
 * @param {string} hexString - hex tagId to normalize
 * @param {RfidConfig} [rfidConfig] - resolved config; `strip0s` gates non-ascii stripping
 * @param {string} [strategy] - per-tagId strategy string ('ascii' or undefined)
 * @returns {string} normalized hex string, or empty string for falsy input
 */
function normalizeHex(hexString, rfidConfig, strategy) {
    if (!hexString) return '';
    const upper = hexString.toUpperCase();
    if (strategy === STRATEGIES.ASCII) {
        return upper.replace(/^(00)+/, '').replace(/(00)+$/, '');
    }
    if (rfidConfig?.strip0s) {
        return upper.replace(/^0+/, '');
    }
    return upper;
}

/**
 * Converts a value to hex using the given strategy.
 * @param {string} value
 * @param {string} strategy
 * @param {object} [record]
 * @returns {string}
 */
function convertToHex(value, strategy, record) {
    switch (strategy) {
        case 'ascii':
            return ascii.toHex(value);
        case '_id': {
            const id = record?._id;
            const idStr = typeof id === 'string' ? id : id?.toHexString?.() || id?.toString?.() || '';
            return `1A${idStr.substring(2) || value}`.toUpperCase();
        }
        case 'gs1':
        case 'gid': {
            const gs1TdsValue = gs1.parse(value);
            return gs1TdsValue?.hex || value;
        }
        case 'hex':
        default:
            return value.replace(/^0+/, '').toUpperCase();
    }
}

/**
 * Gets the tagId for a record, using the configured RFID strategy.
 * Extracted from rfidStrategy.getTagId().
 *
 * @param {object|string} recordOrString - a record object or a string value
 * @param {string} [titularField='title'] - field to use as fallback value
 * @param {RfidConfig} rfidConfig
 * @returns {string} hex value to be used as the tagId
 */
function generateTagId(recordOrString, titularField = 'title', rfidConfig) {
    let value;
    if (typeof recordOrString === 'string') {
        value = recordOrString;
    } else {
        value = recordOrString[titularField] || '';
    }
    if (!value) return '';
    const { maxShortLength, shortStrategy, longStrategy } = rfidConfig;
    if (value.length <= maxShortLength) {
        return convertToHex(value, shortStrategy, recordOrString);
    }
    return convertToHex(value, longStrategy, recordOrString);
}

/**
 * Resolves the RFID configuration from tenant settings and feature flags.
 * Extracted from rfidStrategy.getRfidConfig(), but takes a simple
 * `{ displayInAscii: boolean }` object instead of silo-specific userContext.
 *
 * @param {object} [tenantSettings={}]
 * @param {{ displayInAscii?: boolean, strip0s?: boolean }} [featureFlags={}]
 * @returns {RfidConfig}
 */
function resolveRfidConfig(tenantSettings = {}, featureFlags = {}) {
    let rfidConfig = { ...DEFAULTS };
    if (tenantSettings.rfid) {
        const {
            maxShortLength = rfidConfig.maxShortLength,
            shortStrategy = rfidConfig.shortStrategy,
            longStrategy = rfidConfig.longStrategy,
            displayStrategy = rfidConfig.displayStrategy
        } = tenantSettings.rfid;
        rfidConfig = { maxShortLength, shortStrategy, longStrategy, displayStrategy };
    } else {
        if (featureFlags.displayInAscii) {
            rfidConfig.displayStrategy = 'ascii';
        } else {
            rfidConfig = { ...HEX_DEFAULTS };
        }
    }
    if (tenantSettings.strip0s !== undefined) {
        rfidConfig.strip0s = !!tenantSettings.strip0s;
    } else {
        rfidConfig.strip0s = !!featureFlags.strip0s;
    }
    return rfidConfig;
}

/**
 * Returns true when either the short or long strategy is ascii.
 * @param {RfidConfig} rfidConfig
 * @returns {boolean}
 */
function isAsciiStrategy(rfidConfig) {
    return rfidConfig.shortStrategy === STRATEGIES.ASCII || rfidConfig.longStrategy === STRATEGIES.ASCII;
}

/**
 * Gets the display value for a tagId based on the display strategy.
 * For ascii: decodes hex to ASCII string.
 * For gs1/gid: parses and returns GS1 string representation.
 * For hex: returns the tagId as-is.
 *
 * @param {string} tagId - hex tagId
 * @param {RfidConfig} rfidConfig
 * @returns {string} the human-readable display value
 */
function displayTagId(tagId, rfidConfig) {
    const { displayStrategy } = rfidConfig;
    if (!tagId) return '';
    if (displayStrategy === STRATEGIES.ASCII) {
        // Only decode when the stored hex is unambiguously printable ASCII;
        // otherwise show the raw hex rather than silently dropping/garbling
        // bytes such as 0x96 (1050).
        return canDisplayAsAscii(tagId) ? ascii.fromHex(tagId) : tagId;
    }
    if (displayStrategy === STRATEGIES.GS1 || displayStrategy === STRATEGIES.GID) {
        // Only render as GS1 when the value confidently round-trips as a GS1/EPC
        // tag; otherwise show the raw hex instead of a coincidental parse. Parse
        // once and reuse for both the round-trip check and the display string.
        try {
            const gs1TdsValue = gs1.parse(tagId);
            if (gs1TdsValue?.hex?.toUpperCase() !== tagId.toUpperCase()) return tagId;
            return gs1TdsValue.toString() || tagId;
        } catch {
            return tagId;
        }
    }
    return tagId;
}

/**
 * Builds a MongoDB filter object to find a record by tagId.
 * Handles strategy-specific normalization and optional reverse-lookup
 * by titularField (e.g. assetNo).
 *
 * @param {string} tagId - hex tagId to search for
 * @param {string} [titularField='title'] - field name for reverse-lookup
 * @param {boolean} [simpleSearch=true] - true for tagId-only filter, false to include titularField
 * @param {RfidConfig} rfidConfig
 * @returns {{tagId:string}|{$or:object[]}} a MongoDB filter object
 */
function buildTagIdFilter(tagId, titularField = 'title', simpleSearch = true, rfidConfig) {
    tagId = tagId.toUpperCase();
    const { shortStrategy, longStrategy } = rfidConfig;
    if ([shortStrategy, longStrategy].includes(STRATEGIES.ID) && tagId.startsWith('1A')) {
        if (simpleSearch) {
            return { tagId };
        }
        return { $or: [{ _id: { $regex: new RegExp(`^..${tagId.slice(2)}$`) } }, { tagId }] };
    }

    if ([shortStrategy, longStrategy].includes(STRATEGIES.ASCII)) {
        const trimmedValue = tagId.replace(/^(00)+/, '').replace(/(00)+$/, '');
        if (simpleSearch) {
            return { tagId: trimmedValue };
        }
        const asciiString = ascii.fromHex(tagId);
        if (!/[a-zA-Z0-9]/.test(asciiString)) {
            return { tagId: trimmedValue };
        }
        return { $or: [{ [titularField]: ascii.fromHex(tagId) }, { tagId: trimmedValue }] };
    }

    if ([shortStrategy, longStrategy].includes(STRATEGIES.GS1) || [shortStrategy, longStrategy].includes(STRATEGIES.GID)) {
        if (simpleSearch) {
            return { tagId };
        }
        try {
            const gs1TdsValue = gs1.parse(tagId);
            if (gs1TdsValue.type === types.gid96.id) {
                return { $or: [{ [titularField]: gs1TdsValue.serial.toString() }, { tagId }] };
            }
        } catch {
            // Fall through to default tagId filter
        }
        // todo: GS1 TDS will generally contain multiple fields.
        // this will need further mapping!
        return { tagId };
    }

    // hex: trim leading 0s
    const trimmedValue = tagId.replace(/^0+/, '');
    if (simpleSearch) {
        return { tagId: trimmedValue };
    }
    return { $or: [{ [titularField]: trimmedValue }, { tagId: trimmedValue }] };
}

module.exports = {
    STRATEGIES,
    DEFAULTS,
    HEX_DEFAULTS,
    isValidHex,
    isPrintableAscii,
    canDisplayAsAscii,
    isLikelyGs1Hex,
    normalizeHex,
    generateTagId,
    resolveRfidConfig,
    isAsciiStrategy,
    displayTagId,
    buildTagIdFilter
};
